'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import { brandVars } from '@/lib/color/brand'
import Spinner from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'pill' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  active?: boolean
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  /** Render as a child element (e.g. next/link) while keeping button styling. */
  asChild?: boolean
  /**
   * Paints a `primary` button in a customer's own colour instead of the app's brand token.
   * The label, icons and spinner take whichever ink is legible on that colour, and the ring
   * falls back to a contrast-corrected edge — so a `#FFFFFF` brand still reads as a button.
   */
  brandColor?: string | null
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[14px] gap-1.5 rounded-[9px]',
  md: 'h-9 px-3.5 text-[14px] gap-2 rounded-[10px]',
  lg: 'h-10 px-4 text-[14px] gap-2 rounded-[10px]',
}

const VARIANT: Record<ButtonVariant, string> = {
  // Figma "Button" (Upgrade plan / Send / Add new): brand-2 fill, 30% brand border, brand shadow + inner glow.
  // `--lc-brand-ink` is white on the default blue and flips to ink on the pale accents, so the
  // label follows the fill instead of assuming one.
  primary:
    'relative overflow-hidden bg-brand-2 text-brand-ink [--glass-ink:var(--lc-brand-ink,#fff)] border border-brand/30 shadow-brand-ring ' +
    'before:pointer-events-none before:absolute before:left-1/2 before:top-full before:h-[19px] before:w-[228px] before:-translate-x-1/2 before:-translate-y-[9px] before:rounded-[50%] before:bg-white/10 before:blur-[12px] ' +
    'hover:bg-brand hover:border-brand/45 disabled:hover:bg-brand-2',
  // Figma secondary (Upload / Back / NFC Setup): white, 1px #E9E9E9 ring with soft drop.
  secondary:
    'bg-surface text-ink shadow-input hover:bg-surface-2 dark:hover:bg-surface-3 disabled:hover:bg-surface',
  // Figma "Upload" button: canvas-coloured fill, no ring.
  soft: 'bg-canvas text-ink-2 hover:bg-surface-3 hover:text-ink dark:bg-surface-2 dark:hover:bg-surface-3 disabled:hover:bg-canvas',
  ghost: 'bg-transparent text-ink-2 hover:bg-surface-2 dark:hover:bg-surface-3',
  // Figma nav tab / "Today" chip: 32px, radius 10, active = white + tab shadow.
  pill: 'h-8 px-3 rounded-[10px] text-ink-2 hover:text-ink hover:bg-surface/70 data-[active=true]:bg-surface data-[active=true]:text-ink data-[active=true]:shadow-tab dark:data-[active=true]:bg-surface-3',
  danger:
    'bg-[#EF4444] text-white border border-[rgba(255,255,255,0.2)] hover:bg-[#DC2626] shadow-[2px_4px_12px_0_rgba(239,68,68,0.16)]',
}

/**
 * LiveContact button. Press feedback is `scale(0.97)` at 160ms ease-out (livecontact-emil-design-eng),
 * loading state crossfades content with a 2px blur so the change doesn't feel like a swap.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'sm',
      loading = false,
      active,
      leadingIcon,
      trailingIcon,
      disabled,
      children,
      type = 'button',
      asChild = false,
      brandColor,
      style,
      ...props
    },
    ref
  ) => {
    const isPrimary = variant === 'primary'
    const isBranded = isPrimary && Boolean(brandColor)
    /* Inline values beat the variant's `bg-brand-2`/`text-white`, so a branded button drops the
       colour classes and hovers with brightness instead of a second background class. */
    const brandStyle: React.CSSProperties | undefined = isBranded
      ? ({
          ...brandVars(brandColor),
          backgroundColor: 'var(--brand)',
          borderColor: 'color-mix(in srgb, var(--brand-edge, var(--brand)) 45%, transparent)',
          color: 'var(--brand-on)',
          '--glass-ink': 'var(--brand-on)',
        } as React.CSSProperties)
      : undefined
    const classes = cn(
      'group/btn inline-flex select-none items-center justify-center whitespace-nowrap font-medium leading-none tracking-[-0.2px] outline-none',
      'transition-[transform,background-color,border-color,box-shadow,color] duration-[160ms] ease-out-strong',
      'active:scale-[0.97] focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
      'disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100',
      SIZE[size],
      VARIANT[variant],
      isBranded && 'lc-brand hover:brightness-[1.06]',
      className
    )

    const renderContent = (content: React.ReactNode) => {
      // The glass gradient paints through `color: transparent` + background-clip, so it only works
      // on text — wrapping an icon in it makes its `currentColor` stroke vanish (icon-only Send).
      const isTextContent = React.Children.toArray(content).every(
        (child) => typeof child === 'string' || typeof child === 'number'
      )

      return (
        <>
          <span
            className={cn(
              'inline-flex items-center justify-center gap-[inherit] transition-[opacity,filter] duration-200 ease',
              loading && 'opacity-0 blur-[2px]'
            )}
          >
            {leadingIcon && (
              <span className="inline-flex shrink-0 [&>svg]:size-4">{leadingIcon}</span>
            )}
            {content != null && (
              <span className={cn(isPrimary && isTextContent && 'lc-text-glass')}>{content}</span>
            )}
            {trailingIcon && (
              <span className="inline-flex shrink-0 [&>svg]:size-4">{trailingIcon}</span>
            )}
          </span>
          {loading && (
            <span className="absolute inset-0 inline-flex items-center justify-center">
              <Spinner
                className={cn(
                  'size-4',
                  isBranded
                    ? 'text-[color:var(--brand-on,#fff)]'
                    : isPrimary
                      ? 'text-brand-ink'
                      : 'text-ink-3'
                )}
              />
            </span>
          )}
        </>
      )
    }

    // asChild: hand the styling to the single child (a next/link, <a> or <label>) instead of
    // nesting it inside a <button>, which is invalid markup and breaks label/link activation.
    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<Record<string, unknown>>
      return React.cloneElement(
        child,
        {
          ...props,
          ref,
          style: brandStyle ? { ...brandStyle, ...style } : style,
          className: cn(classes, child.props.className as string | undefined),
          'data-active': active ? 'true' : undefined,
          'data-loading': loading ? 'true' : undefined,
          'aria-busy': loading || undefined,
          'aria-disabled': disabled || loading || undefined,
        } as Record<string, unknown>,
        renderContent(child.props.children as React.ReactNode)
      )
    }

    return (
      <button
        ref={ref}
        type={type}
        data-active={active ? 'true' : undefined}
        data-loading={loading ? 'true' : undefined}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        style={brandStyle ? { ...brandStyle, ...style } : style}
        className={classes}
        {...props}
      >
        {renderContent(children)}
      </button>
    )
  }
)
Button.displayName = 'Button'

export default Button
